<template>
  <div class="popular-answer-board">
    <div class="board">
      <!-- Column categories -->
      <div class="row" style="display: flex; align-items: flex-end">
        <div
          v-for="(column, colIndex) in gameData.columns"
          :key="'col-' + colIndex"
          class="category col-category"
        >
          {{ column.name }}
        </div>
      </div>
      <!-- Row categories and filled board -->
      <div v-for="(row, rowIndex) in filledBoard" :key="rowIndex" class="row">
        <!-- Row category -->
        <div class="category row-category">{{ gameData.rows[rowIndex].name }}</div>
        <!-- Filled board cells -->
        <div v-for="(cell, colIndex) in row" :key="colIndex" class="box">
          <div class="cell-content">
            <!-- Display the rarity at the top -->
            <div
              v-if="cell.rarity"
              class="rarity"
              :style="{ backgroundColor: getRarityColor(cell.rarity) }"
            >
              {{ getRarityText(cell.rarity) }} - {{ cell.rarity }}%
            </div>
            <!-- Display the country flag image -->
            <div class="flag-container">
              <img
                :src="getImageUrl(cell.name)"
                alt="Country Flag"
                v-if="cell.name"
                class="country-flag"
              />
            </div>
            <div v-if="!cell.name" class="no-data">No data</div>
            <!-- Display the country name at the bottom -->
            <div class="country-name">{{ updateOldNamesToMatchCDNNames(cell.name) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import getImageUrl from '@/utils/getImageUrl';
  import { rarityColor, rarityText } from '@/utils/rarityUtils';
  import sanitizeString from '@/utils/sanitizeString';
  import { updateOldNamesToMatchCDNNames } from '@/utils/updateOldCountryName';

  export default {
    props: {
      board: Array,
      liveRarityData: Object,
      gameData: Object,
      countryData: Object,
      answersList: Object,
    },
    data() {
      return {
        imageUrlCache: {},
      };
    },
    computed: {
      // Fill board with guessed countries and rarity percentages
      filledBoard() {
        const filledBoard = [];
        for (let rowIndex = 0; rowIndex < this.board.length; rowIndex++) {
          filledBoard[rowIndex] = [];
          for (let colIndex = 0; colIndex < this.board[rowIndex].length; colIndex++) {
            const matchBox = 'match_box_' + (rowIndex * this.board[rowIndex].length + colIndex + 1);
            const matchData = this.liveRarityData[matchBox];

            if (matchData && typeof matchData === 'object' && Object.keys(matchData).length > 0) {
              // Create an object to store aggregated counts
              const aggregatedData = {};

              for (const answer in matchData) {
                // Skip the 'total' key
                if (answer === 'total') continue;

                const normalizedAnswer = sanitizeString(updateOldNamesToMatchCDNNames(answer));
                if (!aggregatedData[normalizedAnswer]) {
                  aggregatedData[normalizedAnswer] = { count: 0, originalAnswer: answer };
                }
                aggregatedData[normalizedAnswer].count += matchData[answer];
              }

              let mostPopularAnswer = '';
              let highestCount = 0;

              // Normalize the answers list for comparison
              const normalizedAnswersList = this.answersList[matchBox].map((country) => {
                return sanitizeString(updateOldNamesToMatchCDNNames(country));
              });

              for (const normalizedAnswer in aggregatedData) {
                if (
                  (aggregatedData[normalizedAnswer].count > highestCount || highestCount === 0) &&
                  normalizedAnswersList.includes(normalizedAnswer)
                ) {
                  mostPopularAnswer = aggregatedData[normalizedAnswer].originalAnswer; // Keep the original answer for display
                  highestCount = aggregatedData[normalizedAnswer].count;
                }
              }

              const total = matchData.total;
              const rarityPercentage = total ? ((highestCount / total) * 100).toFixed(1) : 0;
              filledBoard[rowIndex][colIndex] = {
                name: mostPopularAnswer,
                rarity: rarityPercentage,
              };
            } else {
              filledBoard[rowIndex][colIndex] = { name: '', rarity: 0 };
            }
          }
        }
        return filledBoard;
      },
    },
    methods: {
      getImageUrl,
      updateOldNamesToMatchCDNNames,

      // Set the color based on the rarity percentage
      getRarityColor(rarityPercentage) {
        return rarityColor(rarityPercentage);
      },

      // Set the rarity text based on the rarity percentage
      getRarityText(rarityPercentage) {
        return rarityText(rarityPercentage);
      },
    },
  };
</script>

<style scoped>
  .popular-answer-board {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }

  .board {
    display: flex;
    flex-direction: column;
    margin-top: 0px;
    gap: 0;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .row:not(:first-child) {
    margin-bottom: -5px;
  }

  .row:first-child {
    margin-left: 145px;
  }

  .category {
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    width: 140px;
    min-height: 50px;
    max-height: 140px;
    margin-top: 5px;
    color: rgb(223, 223, 223);
    font-size: 14px;
    background-color: rgba(255, 255, 255, 0.1);
    padding: 10px;
    box-sizing: border-box;
    border-radius: 16px;
  }

  .box {
    width: 140px;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border: 2px solid transparent;
    border-radius: 16px;
    pointer-events: none;
    box-sizing: border-box;
    padding: 4px;
  }

  .cell-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    position: relative;
    width: 100%;
    height: 100%;
  }

  .rarity {
    width: 100%;
    border-radius: 8px;
    font-size: 10px;
    color: white;
    font-weight: bold;
    line-height: 12px;
    padding: 2px 0;
  }

  .country-name {
    width: 100%;
    border-radius: 8px;
    font-size: 10px;
    color: white;
    font-weight: bold;
    background-color: #373768;
    line-height: 12px;
    padding: 4px 0;
  }

  .flag-container {
    width: 100%;
    flex-grow: 1;
    overflow: hidden;
    padding: 4px;
  }
  .country-flag {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 992px) {
    .board {
      margin-left: 0;
    }
  }

  @media (max-width: 768px) {
    .board {
      display: flex;
      flex-direction: column;
      margin-left: -60px;
    }
    .row {
      margin-left: 45px;
      gap: 4px;
    }
    .row:not(:first-child) {
      margin-bottom: -11px;
    }

    .row:first-child {
      margin-left: 129px;
      margin-bottom: -3px;
    }

    .category {
      width: 105px;
      font-size: 10px;
      min-height: 40px;
      max-height: 105px;
      margin-top: 8px;
    }

    .row-category {
      width: 80px;
    }

    .box {
      width: 105px;
      height: 105px;
    }

    .rarity {
      font-size: 7px;
      padding: 0;
    }

    .country-name {
      font-size: 7px;
      padding: 0;
    }
  }

  @media (max-width: 480px) {
    .board {
      display: flex;
      flex-direction: column;
      margin-left: -60px;
    }
    .row {
      margin-left: 55px;
    }
    .row:first-child {
      margin-left: 135px;
      margin-bottom: 2px;
      margin-top: 2px;
    }
    .row:not(:first-child) {
      margin-bottom: -1px;
    }
    .category {
      width: 85px;
      font-size: 8px;
      min-height: 30px;
      max-height: 85px;
      margin-top: 3px;
    }
    .col-category {
      border-radius: 10px;
    }
    .row-category {
      width: 76px;
    }
    .box {
      width: 85px;
      height: 85px;
    }

    .rarity {
      font-size: 6px;
      line-height: 9px;
    }
    .country-name {
      font-size: 6px;
      line-height: 9px;
    }
  }

  @media (max-width: 375px) {
    .row {
      height: 90px;
    }
  }

  @media (max-width: 768px), (max-width: 480px) {
    body {
      overflow: hidden;
    }
  }
  @media (max-width: 360px) {
    .row {
      margin-left: 55px;
    }
    .row:first-child {
      margin-left: 128px;
    }
    .row:not(:first-child) {
      margin-bottom: -2px;
    }
    .category {
      width: 75px;
      font-size: 8px;
      min-height: 30px;
      max-height: 75px;
      margin-top: 3px;
    }
    .col-category {
      border-radius: 10px;
    }
    .row-category {
      width: 66px;
      border-radius: 10px;
    }
    .box {
      width: 75px;
      height: 75px;
      border-radius: 10px;
    }
  }
</style>
